import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiX, FiPlus, FiEdit, FiTrash2 } from 'react-icons/fi';
import { useAddresses } from '../hooks/useAddresses';
import { useLanguage } from '../context/LanguageContext';
import { supabase } from '../config/supabase';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const CloseButton = styled.button`
  padding: 8px;
  border: none;
  background: none;
  cursor: pointer;
  color: #6b7280;
  font-size: 24px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #1f2937;
  }
`;

const ModalContent = styled.div`
  padding: 24px;
  flex: 1;
  overflow-y: auto;
`;

const AddressManager = styled.div`
  margin-bottom: 24px;
`;

const AddressList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
`;

const AddressCard = styled.div`
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  position: relative;
  transition: all 0.2s;

  &:hover {
    border-color: #d1d5db;
  }
`;

const AddressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 8px;
`;

const AddressTitle = styled.div`
  font-weight: 600;
  color: #1f2937;
`;

const AddressActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #6b7280;
  display: flex;
  align-items: center;

  &:hover {
    color: #5efba9;
  }
`;

const AddressDetails = styled.div`
  color: #6b7280;
  font-size: 14px;
  line-height: 1.6;
`;

const AddNewAddressButton = styled.button`
  width: 100%;
  padding: 12px;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  background: transparent;
  color: #6b7280;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    border-color: #5efba9;
    color: #5efba9;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #34d399;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #34d399;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;

  input {
    cursor: pointer;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const SaveButton = styled.button`
  flex: 1;
  padding: 12px;
  background: #5efba9;
  color: #374151;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #4ce89a;
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 12px;
  background: #6b7280;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #4b5563;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #6b7280;
`;

export default function ManageAddressesModal({ isOpen, onClose }) {
  const { isArabic } = useLanguage();
  const {
    addresses,
    loading: addressesLoading,
    addAddress,
    updateAddress,
    deleteAddress,
  } = useAddresses();

  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [shippingCountries, setShippingCountries] = useState([]);
  const [formData, setFormData] = useState({
    full_name: '',
    phone_number: '',
    email: '',
    area: '',
    city: '',
    governorate: '',
    country: '',
    delivery_notes: '',
    is_default: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchShippingCountries();
    }
  }, [isOpen]);

  const fetchShippingCountries = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('shipping_countries')
        .select('*');

      if (!fetchError && data) {
        setShippingCountries(data);
      }
    } catch (err) {
      console.error('Error fetching shipping countries:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSaveAddress = async () => {
    setError(null);
    setSaving(true);

    try {
      if (!formData.full_name || !formData.phone_number || !formData.area || !formData.city || !formData.governorate || !formData.country) {
        setError(isArabic ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill in all required fields');
        setSaving(false);
        return;
      }

      if (editingAddressId) {
        await updateAddress(editingAddressId, formData);
      } else {
        await addAddress(formData);
      }

      // Reset form
      setIsAddingAddress(false);
      setEditingAddressId(null);
      setFormData({
        full_name: '',
        phone_number: '',
        email: '',
        area: '',
        city: '',
        governorate: '',
        country: '',
        delivery_notes: '',
        is_default: false,
      });
      setError(null);
    } catch (err) {
      console.error('Error saving address:', err);
      setError(err.message || (isArabic ? 'فشل حفظ العنوان' : 'Failed to save address'));
    } finally {
      setSaving(false);
    }
  };

  const handleEditAddress = (address) => {
    setEditingAddressId(address.id);
    setIsAddingAddress(true);
    setFormData({
      full_name: address.full_name || '',
      phone_number: address.phone_number || '',
      email: address.email || '',
      area: address.area || '',
      city: address.city || '',
      governorate: address.governorate || '',
      country: address.country || '',
      delivery_notes: address.delivery_notes || '',
      is_default: address.is_default || false,
    });
    setError(null);
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm(isArabic ? 'هل أنت متأكد من حذف هذا العنوان؟' : 'Are you sure you want to delete this address?')) {
      return;
    }

    try {
      await deleteAddress(addressId);
    } catch (err) {
      console.error('Error deleting address:', err);
      setError(err.message || (isArabic ? 'فشل حذف العنوان' : 'Failed to delete address'));
    }
  };

  const handleCancel = () => {
    setIsAddingAddress(false);
    setEditingAddressId(null);
    setFormData({
      full_name: '',
      phone_number: '',
      email: '',
      area: '',
      city: '',
      governorate: '',
      country: '',
      delivery_notes: '',
      is_default: false,
    });
    setError(null);
  };

  const handleAddNew = () => {
    setEditingAddressId(null);
    setIsAddingAddress(true);
    setFormData({
      full_name: '',
      phone_number: '',
      email: '',
      area: '',
      city: '',
      governorate: '',
      country: '',
      delivery_notes: '',
      is_default: false,
    });
    setError(null);
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{isArabic ? 'إدارة العناوين' : 'Manage Addresses'}</ModalTitle>
          <CloseButton onClick={onClose}>
            <FiX />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          {addressesLoading ? (
            <EmptyState>{isArabic ? 'جاري تحميل العناوين...' : 'Loading addresses...'}</EmptyState>
          ) : addresses.length === 0 && !isAddingAddress ? (
            <EmptyState>
              <p>{isArabic ? 'لا توجد عناوين بعد.' : 'No addresses yet.'}</p>
              <AddNewAddressButton onClick={handleAddNew} style={{ marginTop: '20px', maxWidth: '300px', margin: '20px auto 0' }}>
                <FiPlus /> {isArabic ? 'إضافة عنوان جديد' : 'Add New Address'}
              </AddNewAddressButton>
            </EmptyState>
          ) : (
            <>
              {!isAddingAddress && (
                <AddressManager>
                  <AddressList>
                    {addresses.map((address) => (
                      <AddressCard key={address.id}>
                        <AddressHeader>
                          <AddressTitle>
                            {address.full_name || address.phone_number}
                            {address.is_default && (
                              <span
                                style={{
                                  color: '#5efba9',
                                  fontSize: '12px',
                                  marginLeft: '8px',
                                }}
                              >
                                ({isArabic ? 'افتراضي' : 'Default'})
                              </span>
                            )}
                          </AddressTitle>
                          <AddressActions onClick={(e) => e.stopPropagation()}>
                            <ActionButton
                              onClick={() => handleEditAddress(address)}
                              title={isArabic ? 'تعديل' : 'Edit'}
                            >
                              <FiEdit />
                            </ActionButton>
                            <ActionButton
                              onClick={() => handleDeleteAddress(address.id)}
                              title={isArabic ? 'حذف' : 'Delete'}
                            >
                              <FiTrash2 />
                            </ActionButton>
                          </AddressActions>
                        </AddressHeader>
                        <AddressDetails>
                          <div>{address.phone_number}</div>
                          {address.email && <div>{address.email}</div>}
                          <div>
                            {address.area}, {address.city}, {address.governorate}
                            {address.country ? `, ${address.country}` : ''}
                          </div>
                          {address.delivery_notes && (
                            <div style={{ marginTop: '4px' }}>
                              <em>{address.delivery_notes}</em>
                            </div>
                          )}
                        </AddressDetails>
                      </AddressCard>
                    ))}
                  </AddressList>
                </AddressManager>
              )}

              {!isAddingAddress && (
                <AddNewAddressButton onClick={handleAddNew}>
                  <FiPlus /> {isArabic ? 'إضافة عنوان جديد' : 'Add New Address'}
                </AddNewAddressButton>
              )}

              {isAddingAddress && (
                <>
                  <h3 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: '600' }}>
                    {editingAddressId
                      ? isArabic
                        ? 'تعديل العنوان'
                        : 'Edit Address'
                      : isArabic
                      ? 'إضافة عنوان جديد'
                      : 'Add New Address'}
                  </h3>

                  {error && (
                    <div
                      style={{
                        padding: '12px',
                        background: '#fee2e2',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        color: '#991b1b',
                        marginBottom: '20px',
                        fontSize: '14px',
                      }}
                    >
                      {error}
                    </div>
                  )}

                  <FormGroup>
                    <Label>{isArabic ? 'الاسم الكامل *' : 'Full Name *'}</Label>
                    <Input
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      placeholder={isArabic ? 'أدخل الاسم الكامل' : 'Enter your full name'}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{isArabic ? 'رقم الهاتف *' : 'Phone Number *'}</Label>
                    <Input
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={handleInputChange}
                      type="tel"
                      placeholder="+968 XXXX XXXX"
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{isArabic ? 'البريد الإلكتروني (اختياري)' : 'Email (Optional)'}</Label>
                    <Input
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      type="email"
                      placeholder="your@email.com"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{isArabic ? 'المنطقة / الشارع *' : 'Area / Street *'}</Label>
                    <Input
                      name="area"
                      value={formData.area}
                      onChange={handleInputChange}
                      type="text"
                      placeholder={isArabic ? 'أدخل المنطقة أو اسم الشارع' : 'Enter area or street name'}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{isArabic ? 'المدينة *' : 'City *'}</Label>
                    <Input
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      type="text"
                      placeholder={isArabic ? 'أدخل المدينة' : 'Enter city'}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{isArabic ? 'المحافظة *' : 'Governorate *'}</Label>
                    <Input
                      name="governorate"
                      value={formData.governorate}
                      onChange={handleInputChange}
                      type="text"
                      placeholder={isArabic ? 'أدخل المحافظة' : 'Enter governorate'}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>{isArabic ? 'البلد *' : 'Country *'}</Label>
                    <Input
                      as="select"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      required
                      style={{
                        padding: '12px 16px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '14px',
                        backgroundColor: 'white',
                        cursor: 'pointer',
                        width: '100%',
                        boxSizing: 'border-box',
                      }}
                    >
                      <option value="">{isArabic ? 'اختر البلد' : 'Select country'}</option>
                      {shippingCountries.map((country) => (
                        <option key={country.code} value={country.name}>
                          {country.name}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>

                  <FormGroup>
                    <Label>{isArabic ? 'ملاحظات التسليم (اختياري)' : 'Delivery Notes (Optional)'}</Label>
                    <Textarea
                      name="delivery_notes"
                      value={formData.delivery_notes}
                      onChange={handleInputChange}
                      placeholder={isArabic ? 'أي تعليمات خاصة للتسليم' : 'Any special instructions for delivery'}
                    />
                  </FormGroup>

                  <FormGroup>
                    <CheckboxLabel>
                      <input
                        type="checkbox"
                        checked={formData.is_default}
                        onChange={handleInputChange}
                        name="is_default"
                      />
                      <span>{isArabic ? 'تعيين كعنوان افتراضي' : 'Set as default address'}</span>
                    </CheckboxLabel>
                  </FormGroup>

                  <ButtonGroup>
                    <SaveButton onClick={handleSaveAddress} disabled={saving}>
                      {saving
                        ? isArabic
                          ? 'جاري الحفظ...'
                          : 'Saving...'
                        : editingAddressId
                        ? isArabic
                          ? 'تحديث العنوان'
                          : 'Update Address'
                        : isArabic
                        ? 'حفظ العنوان'
                        : 'Save Address'}
                    </SaveButton>
                    <CancelButton onClick={handleCancel}>
                      {isArabic ? 'إلغاء' : 'Cancel'}
                    </CancelButton>
                  </ButtonGroup>
                </>
              )}
            </>
          )}
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
}

